import type { TFunction } from 'i18next'

import { ItemType } from '#/shared/api/generated/communication/models'
import type {
  ConversationListItem,
  LastItem,
} from '#/shared/api/generated/communication/models'
import { formatPhonesInText } from '#/shared/lib/format'

import type { PhoneActivityItem } from '../model/types'
import { counterpartyTitle } from './customer-display'
import { mainlineRouteLabel } from './mainline'
import { activityTimestamp, dateGroupLabel } from './relative-date'

/** Delivery status that marks a thread's last item as failed-to-send. */
const FAILED_STATUS = 'failed'
const OUTBOUND = 'outbound'

/** Preview text for a thread's last item; media with no caption shows "Photo".
 *  Backend previews embed raw E.164 numbers ("Declined call from +1302…") while
 *  the client-composed route suffix is already formatted — format them so every
 *  number on the row reads the same. */
function previewBody(last: LastItem, t: TFunction): string {
  if (last.preview) return formatPhonesInText(last.preview)
  if (last.type === ItemType.mms) return t('phonePanel.preview.media')
  return ''
}

/** Secondary line for a thread row, derived from its most recent item. */
function previewLine(last: LastItem | null | undefined, t: TFunction): string {
  if (!last) return ''
  if (last.direction === OUTBOUND && last.status === FAILED_STATUS) {
    return t('phonePanel.preview.failed')
  }
  const body = previewBody(last, t)
  if (!body) return ''
  return last.direction === OUTBOUND
    ? t('phonePanel.preview.you', { text: body })
    : body
}

/**
 * Routing suffix for a shared-mainline thread, e.g. "(305) 880-4412 → (929)
 * 592-3003", ordered by the last item's direction (outbound: mainline first).
 * Surfaced so the shared line a conversation lives on is visible in the list;
 * personal-line threads carry no mainline and omit it.
 */
function mainlineRoute(conversation: ConversationListItem): string | null {
  const { mainline } = conversation
  if (!mainline) return null
  return mainlineRouteLabel(
    conversation.to_number,
    mainline,
    conversation.last_item?.direction,
  )
}

/**
 * Thread title for the list: the counterparty name suffixed with the shared
 * mainline's label, e.g. "Maria Lopez · Mainline 2", so the agent sees which
 * line a thread belongs to. Personal-line threads carry no mainline and show
 * just the name/number.
 */
function threadTitle(conversation: ConversationListItem): string {
  const base = counterpartyTitle(conversation.customer, conversation.to_number)
  const label = conversation.mainline?.label
  return label ? `${base} · ${label}` : base
}

/** Map a conversation thread to an activity-list row. */
export function conversationToActivityItem(
  conversation: ConversationListItem,
  t: TFunction,
): PhoneActivityItem {
  const last = conversation.last_item
  const at = last?.at ?? conversation.updated_at
  const failed = last?.direction === OUTBOUND && last.status === FAILED_STATUS
  // For a shared mainline, append the "counterparty → mainline" route after the
  // last-item preview so each row shows which line it belongs to (per Figma).
  const route = mainlineRoute(conversation)
  const preview = previewLine(last, t)

  return {
    id: conversation.id,
    conversationId: conversation.id,
    userId: conversation.user?.id ?? null,
    phone: conversation.to_number,
    title: threadTitle(conversation),
    subtitle: route ? [preview, route].filter(Boolean).join(' · ') : preview,
    timestamp: activityTimestamp(at),
    dateGroup: dateGroupLabel(at, t),
    starred: conversation.is_favorite,
    muted: conversation.is_muted,
    blocked: conversation.is_blocked,
    deliverability: conversation.customer,
    aiTagged: last?.ai_handled,
    unreadCount: conversation.unread_count,
    danger: failed,
  }
}
